import React, { useState, useEffect } from 'react';
import { useWeb3 } from '../contexts/Web3Context';
import { sendContractTransaction, formatTransactionError } from '../utils/transactionHelper';
import './UserRegistration.css';

const UserRegistration = () => {
  const web3Context = useWeb3();
  const { account, contract, web3 } = web3Context;

  const [isRegistered, setIsRegistered] = useState(false);
  const [sponsorAddress, setSponsorAddress] = useState('');
  const [loading, setLoading] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [registerBonus, setRegisterBonus] = useState('0');
  const [sponsorOf, setSponsorOf] = useState('0x0000000000000000000000000000000000000000');
  const [copiedLink, setCopiedLink] = useState(false);

  useEffect(() => {
    // Check URL parameters for sponsor address
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref') || params.get('sponsor');
    if (ref && web3?.utils?.isAddress(ref)) {
      setSponsorAddress(ref);
    }
  }, [web3]);

  useEffect(() => {
    if (account && contract) {
      checkRegistration();
      getRegisterBonus();
    }
  }, [account, contract]);

  const checkRegistration = async () => {
    if (!contract || !account) return;

    try {
      const [user, sponsor] = await Promise.all([
        contract.methods.users(account).call(),
        contract.methods.sponsorOf(account).call()
      ]);

      setIsRegistered(user.registered);
      if (user.registered) {
        setUserInfo(user);
        setSponsorOf(sponsor);
      }
    } catch (error) {
      console.error('Error checking registration:', error);
    }
  };

  const getRegisterBonus = async () => {
    if (!contract || !web3) return;

    try {
      const bonus = await contract.methods.registerBonus().call();
      setRegisterBonus(web3.utils.fromWei(bonus.toString(), 'ether'));
    } catch (error) {
      console.error('Error getting register bonus:', error);
    }
  };

  const handleRegister = async () => {
    if (!contract || !account) {
      alert('Please connect your Web3 wallet first');
      return;
    }

    setLoading(true);
    try {
      const sponsor = web3.utils.isAddress(sponsorAddress)
        ? sponsorAddress
        : '0x0000000000000000000000000000000000000000';

      const method = contract.methods.register(sponsor);
      await sendContractTransaction(web3Context, method);

      alert('Registration successful! Welcome to P2PTrade.');
      checkRegistration();
    } catch (error) {
      console.error('Registration error:', error);
      alert('Registration failed: ' + formatTransactionError(error));
    } finally {
      setLoading(false);
    }
  };

  const copyReferralLink = () => {
    const link = `${window.location.origin}${window.location.pathname}?ref=${account}`;
    navigator.clipboard.writeText(link);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2500);
  };

  if (!account) {
    return (
      <div className="registration-wrapper">
        <div className="registration-card text-center">
          <div className="icon-lock">🔐</div>
          <h2>Wallet Connection Required</h2>
          <p>Please connect your Web3 wallet to register or view account referral statistics.</p>
        </div>
      </div>
    );
  }

  if (isRegistered) {
    const isSponsorValid = sponsorOf && sponsorOf !== '0x0000000000000000000000000000000000000000';

    return (
      <div className="registration-wrapper">
        <div className="registration-card">
          <div className="registered-header">
            <span className="badge-active">✅ Registered Account</span>
            <h2>Member Dashboard Profile</h2>
          </div>

          <div className="user-info-grid">
            <div className="info-tile">
              <span className="tile-label">Account Status</span>
              <span className={`tile-val ${userInfo?.active ? 'active-text' : 'inactive-text'}`}>
                {userInfo?.active ? 'Active Trader' : 'Suspended / Inactive'}
              </span>
            </div>

            <div className="info-tile">
              <span className="tile-label">Trade Bonus Claimed</span>
              <span className="tile-val highlight-val">
                {userInfo ? web3.utils.fromWei(userInfo.tradeBonus.toString(), 'ether') : '0'} Tokens
              </span>
            </div>

            <div className="info-tile">
              <span className="tile-label">Upline Sponsor</span>
              <span className="tile-val address-code">
                {isSponsorValid ? `${sponsorOf.slice(0, 8)}...${sponsorOf.slice(-6)}` : 'Direct Register (No Sponsor)'}
              </span>
            </div>

            <div className="info-tile">
              <span className="tile-label">Team Business Volume</span>
              <span className="tile-val volume-val">
                {userInfo ? web3.utils.fromWei(userInfo.businessVolume.toString(), 'ether') : '0'} USDT
              </span>
            </div>
          </div>

          {/* Referral Link Box */}
          <div className="referral-box">
            <h4>Your Personal Referral Link</h4>
            <p>Share your referral link with new users to build your team and earn multi-level bonuses!</p>
            <div className="link-input-group">
              <input
                type="text"
                readOnly
                value={`${window.location.origin}${window.location.pathname}?ref=${account}`}
              />
              <button className="copy-link-btn" onClick={copyReferralLink}>
                {copiedLink ? '✓ Copied!' : '📋 Copy Link'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="registration-wrapper">
      <div className="registration-card">
        <div className="form-header">
          <h2>Create P2P Account</h2>
          <p>Join the decentralized P2P trading network and start earning multi-level rewards.</p>
        </div>

        {parseFloat(registerBonus) > 0 && (
          <div className="bonus-callout">
            🎁 <strong>Welcome Bonus:</strong> Receive <strong>{registerBonus} Tokens</strong> directly to your wallet upon registration!
          </div>
        )}

        <div className="form-group">
          <label>Sponsor Address (Optional)</label>
          <input
            type="text"
            placeholder="0x... (Leave empty if no sponsor)"
            value={sponsorAddress}
            onChange={(e) => setSponsorAddress(e.target.value)}
            disabled={loading}
          />
          <small className="help-text">If you arrived via a referral link, the sponsor address is auto-filled.</small>
        </div>

        <button
          className="btn-submit-register"
          onClick={handleRegister}
          disabled={loading}
        >
          {loading ? 'Processing Registration...' : 'Register Account Now'}
        </button>
      </div>
    </div>
  );
};

export default UserRegistration;